import { createPortal } from "react-dom";
import { Trans, useTranslation } from "react-i18next";
import { useScopedDraftState } from "./draft-scope.js";
import type React from "react";
import { Fragment, useState } from "react";
import { Boxes, ClipboardPaste, Copy, CopyPlus, Paperclip, Plus, Trash2, X } from "lucide-react";
import type { ArElementSetting, ManualArElement, ManualBody } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Disclosure } from "@/components/ui/disclosure";
import { SETTING_FILE_TYPES } from "./setting-file-types.js";
import { fileMatches, useSideDrop, type DroppedFile } from "./side-drag.js";
import { SECTION_LABELS, isInlineSetting, useBodySections, type SectionState } from "./body-sections.js";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label, TextField } from "react-aria-components";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { useElementInfo } from "./element-info.js";
import { cn } from "@/lib/utils";
import { useTruncated } from "@/lib/useTruncated";
import { htmlToText } from "@/lib/html";
import { DeferredActionButton } from "@/components/ui/deferred-action-button";
import {
  channelLabels,
  DUAL_CHOICES,
  dualKey,
  readDual,
  readMulti,
  readRemote,
  readSwitch,
  writeDual,
  writeMulti,
  writeRemote,
  writeSwitch,
} from "./sensor-setting-model.js";

/**
 * The AR block of a step card (ADR-027 §9 layout — the owner's own tweak after Phases C/D).
 *
 * The card IS the selected element's body; the strip of element tabs runs along the BOTTOM, exactly as the legacy card
 * does. The strip is painted on top of the card since 2026-11-02 (owner), the same order as in the DOM, so keyboard and
 * screen-reader flow remains "pick the element, then its body".
 *
 * **Structure follows the legacy card; the LABELS stay English** like the rest of this editor. ▶ Owner to confirm if
 * the AR vocabulary should be German for author familiarity; that is an app-wide i18n decision, not a per-block one.
 *
 * **Geometry is READ-ONLY here, by owner decision.** Position/rotation/size are authored in Unity against the real 3D
 * scene; a number typed into a web form has no scene to be correct in. It is ALWAYS visible (owner, §9) — "is this
 * thing actually placed, and where" is the question authors ask of this card.
 */
export function StepArStrip({
  elements,
  disabled,
  selectedId,
  onSelect,
  onAddElement,
  busy = false,
  actionsSlotRef,
}: {
  /** The element whose body is showing. */
  selectedId: number | null;
  onSelect: (elementId: number) => void;
  elements: ManualArElement[];
  disabled: boolean;
  /** Open the "add an XR element" picker drawer. Absent ⇒ deferred. */
  onAddElement?: () => void;
  /** An add/remove for THIS step is in flight — the add goes inert (diff-panel AR-P3d Medium #1). Removing is the element's own action now. */
  busy?: boolean;
  /** Where the selected element's actions land: the end of this row, so they need no line of their own. */
  actionsSlotRef?: (element: HTMLElement | null) => void;
}) {
  const { t } = useTranslation();
  // Resolved across the WHOLE step, so an ordinal counts every element an author can see here.
  const labels = elementLabels(elements);
  // Only the main element: nothing to switch between, so no tab — its name as a label, and the add says what it adds.
  // The row keeps its height and place, so nothing jumps when the first XR element arrives (design pass 2026-11-04).
  const onlyMain = elements.length === 1 && elements[0]!.isMain;
  return (
    /* A strip bleeding to the card's edge, on top of what it switches (owner 2026-11-02): at the card's bottom it was out of
       sight on a long card. Tab reaches "which element" before the element's body. The caller's padding is what it bleeds over. */
    <div className="@container border-border/70 bg-muted/60 -mx-4 -mt-4 flex flex-col gap-3 border-b px-4 py-2.5 dark:bg-black/10">
      {/* One row that scrolls sideways where the STRIP is narrow — a phone, or the card in the 3D panel (a wrap took three rows
          there before any content); it wraps where there is room. Measured on the strip itself, so every host gets it. */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Tabs + the add stay on one line on a phone (the tabs scroll); the element's actions wrap below them. */}
        <div className="flex min-w-0 items-center gap-2 @max-3xl:basis-full @3xl:contents">
        {onlyMain ? (
          <MainElementLabel element={elements[0]!} label={labels.get(elements[0]!.id) ?? elementLabel(elements[0]!)} />
        ) : (
          <div className="flex items-center gap-2 [scrollbar-width:none] @max-3xl:-my-1 @max-3xl:min-w-0 @max-3xl:flex-initial @max-3xl:snap-x @max-3xl:overflow-x-auto @max-3xl:py-1 @max-3xl:[&>*]:shrink-0 @max-3xl:[&>*]:snap-start @3xl:contents">
            {elements.map((el) => (
              <ElementChip key={el.id} element={el} label={labels.get(el.id) ?? elementLabel(el)} selected={el.id === selectedId} onSelect={() => onSelect(el.id)} />
            ))}
          </div>
        )}
        {onAddElement && !disabled ? (
          onlyMain ? (
            // The first XR element: orange icon + text, as every other "add content" (owner rule 2026-10-22).
            <Button variant="accent" size="sm" isDisabled={busy} onPress={onAddElement} className="gap-1.5 shadow-sm">
              <Plus aria-hidden="true" />
              {t("manualEditor.addXrElement")}
            </Button>
          ) : (
            // Orange: it adds content. Icon only (owner 2026-11-02), the one exception to "orange = icon + text".
            <TooltipTrigger delay={0} focusable={false}>
              <Button variant="accent" size="icon" aria-label={t("manualEditor.addElement")} isDisabled={busy} onPress={onAddElement} className="rounded-full shadow-sm transition-transform duration-200 hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100">
                <Plus aria-hidden="true" />
              </Button>
              <Tooltip>{t("manualEditor.addElement")}</Tooltip>
            </TooltipTrigger>
          )
        ) : (
          <DeferredActionButton
            label={t("manualEditor.addElement")}
            tooltip={t("manualEditor.elementPickerSoon")}
            variant="outline"
            className="dark:border-border"
            icon={<Plus aria-hidden="true" className="size-3.5" />}
          />
        )}
        </div>
        {actionsSlotRef ? <div ref={actionsSlotRef} className="ml-auto flex flex-wrap items-center justify-end gap-1 empty:hidden" /> : null}
      </div>
    </div>
  );
}

/** The body's thumbnail — icon ONLY; the name is the heading beside it, so `ElementTile` would print it twice. */
/**
 * The element's tile — big, like the legacy's — and for the MAIN element the tile IS the picker (owner, §9: "by
 * clicking it we can change, so we do not need the Choose button"). A bare clickable image is an invisible
 * affordance, which is why the legacy keeps a red `Auswählen` under its tile; here the tile is a real, named button
 * with a hover/focus ring and a "Change" caption, so the affordance is visible AND announced.
 */
function ElementTile({ element, onChange }: { element: ManualArElement | null; onChange?: () => void }) {
  const { t } = useTranslation();
  const icon = element?.iconUrl ? (
    <img src={element.iconUrl} alt="" className="size-6 rounded object-contain" />
  ) : (
    <Boxes aria-hidden="true" className="text-muted-foreground size-4" />
  );
  if (!onChange) {
    return <span className="border-border bg-muted/40 flex size-8 shrink-0 items-center justify-center rounded-lg border">{icon}</span>;
  }
  if (element) {
    // A named control, not a bare thumbnail: "Typ ändern" was hard to find as an icon (owner 2026-11-03).
    return (
      <Button variant="outline" size="sm" onPress={onChange} className="dark:border-border bg-background gap-1.5">
        {element.iconUrl ? <img src={element.iconUrl} alt="" className="size-4 rounded-sm object-contain" /> : <Boxes aria-hidden="true" />}
        {t("manualEditor.changeType")}
      </Button>
    );
  }
  return (
    <button
      type="button"
      onClick={onChange}
      aria-label={element ? t("manualEditor.changeMainElement") : t("manualEditor.chooseMainElement")}
      className={cn(
        "group/tile hover:border-primary focus-visible:ring-primary flex shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border p-0 transition-colors focus-visible:ring-2 focus-visible:outline-none",
        element ? "border-border bg-muted/40 size-8" : "border-border bg-muted/20 size-24 border-dashed",
      )}
    >
      {element ? icon : <Plus aria-hidden="true" className="text-muted-foreground size-8" />}
      {element ? null : <span className="text-muted-foreground group-hover/tile:text-primary text-[0.625rem] leading-none">{t("manualEditor.choose")}</span>}
    </button>
  );
}

/** An element action as an icon with its name in a tooltip and as its accessible name (owner 2026-11-03: as text they took the row). */
function IconAction({ label, icon, onPress, isDisabled = false, destructive = false }: { label: string; icon: React.ReactNode; onPress?: () => void; isDisabled?: boolean; destructive?: boolean }) {
  return (
    <TooltipTrigger delay={300} focusable={false}>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={label}
        isDisabled={isDisabled}
        onPress={onPress}
        className={cn("text-muted-foreground hover:text-foreground", destructive && "hover:bg-destructive/10 hover:text-destructive")}
      >
        {icon}
      </Button>
      <Tooltip>{label}</Tooltip>
    </TooltipTrigger>
  );
}

/** A step's only element, the main one: a label in the tab row, not a tab with nothing beside it to switch to. */
function MainElementLabel({ element, label }: { element: ManualArElement; label: string }) {
  const { t } = useTranslation();
  return (
    <span className="flex h-10 min-w-0 items-center gap-2 px-0.5">
      {element.iconUrl ? <img src={element.iconUrl} alt="" className="size-5 rounded-sm object-contain" /> : <Boxes aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />}
      <span className="flex min-w-0 flex-col">
        <ElementName name={label} className="text-sm font-medium" />
        <span className="text-muted-foreground text-xs leading-none">{t("manualEditor.mainElementShort")}</span>
      </span>
    </span>
  );
}

/** One element on the strip. */
function ElementChip({
  element,
  label,
  selected,
  onSelect,
}: {
  element: ManualArElement;
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  return (
    /* The TAB itself. `aria-pressed` rather than a `tablist`: the body below is not a panel that swaps in place —
       on the main tab it also governs the step's own instruction/media editors, which live in `StepCard`.
       No × on the tab (owner 2026-11-03): it was clicked by accident. Deleting is the element's own action.
       One height for every tab, with or without a subline (owner 2026-11-03). */
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        "focus-visible:ring-primary focus-visible:ring-offset-background flex h-10 min-w-0 cursor-pointer items-center gap-2 rounded-lg border px-2.5 text-left transition-[background-color,border-color,color,box-shadow] duration-200 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none motion-reduce:transition-none",
        // Filled, like every other selection in the editor: a lighter border alone read as not selected (owner 2026-11-02).
        selected ? "border-primary bg-primary text-primary-foreground font-semibold shadow-sm" : "border-border bg-background hover:border-ring/60 hover:bg-muted/60",
      )}
    >
        {element.iconUrl ? (
          <img src={element.iconUrl} alt="" className="size-4 rounded-sm object-contain" />
        ) : (
          <Boxes aria-hidden="true" className={cn("size-4 shrink-0", !selected && "text-muted-foreground")} />
        )}
        <span className="flex min-w-0 flex-col">
          <ElementName name={label} className="max-w-[12rem] text-xs" />
          {/* A subline only where it carries information (owner, §9): "main", or the AMBER "not placed" — an unplaced
              element does not show up in AR at all, so it is worth seeing without opening the tab. A placed XR
              element gets no subline; that is the normal case and the strip reads calmer without it. */}
          {element.isMain ? (
            <span className={cn("text-[0.625rem] leading-none", !selected && "text-muted-foreground")}>{t("manualEditor.mainBadge")}</span>
          ) : element.hasBeenPositioned ? null : (
            <span className={cn("text-[0.625rem] leading-none", !selected && "text-attention")}>{t("manualEditor.notPlaced")}</span>
          )}
        </span>
    </button>
  );
}

/**
 * The selected element's BODY (ADR-027 Phase A) — identity, placement, and one section per setting.
 *
 * **Everything here is DISPLAY-ONLY**, deliberately (plan panel #1). The only writers that exist today are
 * step-scoped: `editStepText` and the media writer both resolve their target as `step.mainElementId`, so wiring the
 * existing editors to a non-main tab would silently overwrite the MAIN element's instruction and media while this tab
 * looked unchanged. Editing arrives with the element-scoped PATCH in Phase B.
 */
export function StepArElementBody({
  element,
  siblings,
  onPickMain,
  disabled,
  onSetSetting,
  onPickSettingFile,
  busySetting,
  copiedFrom,
  onCopySettings,
  onPasteSettings,
  onDuplicate,
  onDelete,
  actionsSlot,
  sections,
  initialStateSensorIds,
  children,
}: {
  element: ManualArElement;
  /** The strip's slot for this element's actions. Without one they stay in the body. */
  actionsSlot?: HTMLElement | null;
  /** Sensors this STEP is the first to use (ADR-037 D7) — the editor computes it once for the whole manual. */
  initialStateSensorIds?: ReadonlySet<number>;
  /** The element's collapsible-section state (ADR-027 §11). The MAIN element's card shares one across its body
   *  sections and its remaining settings so there is ONE "Alle ausklappen" per element, as in the legacy; any other
   *  element gets its own here. */
  sections?: SectionState;
  /** The main element's body sections (rendered by the card), placed under the toggle, before the settings. */
  children?: React.ReactNode;
  /** Every element on this step — `Path` picks its endpoints from them (they are same-step in all measured data). */
  siblings: ManualArElement[];
  onPickMain?: () => void;
  disabled: boolean;
  /** Write one setting's scalar value. Absent ⇒ every setting stays read-only (Phase A behaviour). */
  onSetSetting?: (name: string, patch: { value?: string | null; fileIds?: number[] }) => void;
  /** Open the asset library to attach a file-valued setting — `add` appends to a multi-file list, `replace` swaps. */
  onPickSettingFile?: (name: string, mode: "add" | "replace", dropped?: DroppedFile) => void;
  /** A setting write for THIS element is in flight — controls go inert, as the tab bar's do. */
  busySetting?: boolean;
  /** The legacy's settings clipboard (`Einstellungen kopieren / einfügen`) — what is currently copied, if anything. */
  copiedFrom?: { elementId: number; label: string; settingNames: string[] } | null;
  onCopySettings?: () => void;
  onPasteSettings?: () => void;
  /** The legacy `Element duplizieren` — a deep copy onto this step. Absent (or a main element) ⇒ no control. */
  onDuplicate?: () => void;
  /** Delete this element, the same as the `x` on its tab. Never offered on the main element, which carries the step's content. */
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const [confirmPaste, setConfirmPaste] = useState(false);
  const label = elementLabel(element);
  // The MAIN element's `Text`/`Image`/`Video` ARE the step's content — `assembleBody` derives `step.content` from
  // exactly those three settings, and the Instruction editor + media column already own them. Rendering them here
  // too showed the same video twice, with two different Replace buttons over one file (found in the browser, xr-area
  // 2948 step 1). A NON-main element keeps them: on a `Bild`/`Text`/`Video` XR element the settings ARE its body.
  // DEDUPED BY NAME, first-wins — and this is a data-loss fix, not tidying (diff panel, High).
  //
  // Legacy data holds duplicate `(element, setting)` rows (no unique constraint) and the body read returns every
  // one of them, ascending by id. Rendering both gave two editable controls for one setting, colliding on
  // `key={setting.name}` — but the real damage is downstream: every control addresses its write BY NAME, and the
  // API canonicalizes to the LOWEST id (`ORDER BY s.id LIMIT 1`) and tombstones THAT row's attachments. So
  // removing the file shown in the second control silently destroyed the FIRST row's media. Wrong file gone, no
  // error, nothing to undo it with.
  //
  // The read is id-ascending, so the first occurrence IS the row every write targets — the same rule
  // `settingsOf` follows for the outbound diff (ADR-044 §10 D21). Showing a row no write can reach was the bug.
  const canonical = element.settings.filter((s, i) => element.settings.findIndex((o) => o.name === s.name) === i);
  const settings = element.isMain ? canonical.filter((s) => !MAIN_CONTENT_SETTINGS.has(s.name)) : canonical;
  const own = useBodySections(element.id, settings.map((x) => x.name).filter((n) => !isInlineSetting(n, onSetSetting != null)));
  const state = sections ?? own;
  // Each action's guard EXACTLY ONCE. The row's own condition used to repeat them, so a guard could be removed from a
  // control and the row would still hide it — a mutation test that passes for the wrong reason (§10 fold).
  const canCopy = onCopySettings != null && !disabled && element.settings.length > 0;
  // `Element duplizieren` — never for the MAIN element: our model has one main per step, so a copy would be a headless
  // content carrier the picker can never select (▶ owner; the legacy has no guard).
  // …and never while a structural write on this scope is in flight (`busySetting` carries the strip's busy too): a
  // Duplicate clicked during an add diffed a pre-add snapshot against a body carrying BOTH new elements and selected
  // the add's tab, not the copy's (diff-panel 2b Medium).
  const canDuplicate = onDuplicate != null && !disabled && !busySetting && !element.isMain;
  // Paste only where the two elements SHARE a setting name: the server rejects a paste with nothing to copy, and an
  // offered control that can only fail is worse than none (diff-panel R3 High #1).
  // `!busySetting` for the SAME reason Duplicate has it, and the diff panel caught that paste was missing it:
  // each write handler snapshots its diff baseline from the query cache at CALL time, and the write gate
  // serializes the WRITES but not their baselines. Edit Text old->new, then paste from a source also holding
  // "old" before the first write's cache update: W1 tells Unity "new", W2 restores "old" but diffs equal against
  // its stale baseline and tells Unity nothing. Storage ends at "old" while Unity renders "new" — a persistent
  // desync of the very contract D12 exists to keep.
  const canPaste =
    onCopySettings != null &&
    !disabled &&
    !busySetting &&
    copiedFrom != null &&
    copiedFrom.elementId !== element.id &&
    element.settings.some((x) => isCopyableSetting(x.name) && copiedFrom.settingNames.includes(x.name));
  const actions = (
    <>
      {/* The main element's type, named where the settings start (owner 2026-11-03): the icon-only tile was hard to find. */}
      {element.isMain && onPickMain && !disabled ? <ElementTile element={element} onChange={onPickMain} /> : null}
      {canCopy ? <IconAction label={t("manualEditor.copySettings")} icon={<Copy aria-hidden="true" />} onPress={onCopySettings} /> : null}
      {canPaste ? (
        confirmPaste ? (
          /* Paste OVERWRITES every setting this element shares with the source — on a main element that includes
             the step's own text and media. The legacy gates its (transform) paste behind a yes/no too
             (`overwritePositionWarning`). Inline rather than a modal: a nested react-aria overlay inside the
             editor's own overlay is the shape that produced the `PublishedManualGate` bounce-out. */
          <span className="border-attention/40 bg-attention/10 flex flex-wrap items-center gap-2 rounded-lg border px-2 py-1">
            <span className="text-xs">
              <Trans i18nKey="manualEditor.overwriteElementSettingsFrom" components={{ from: <>{copiedFrom.label}</> }} />
            </span>
            <Button
              variant="default"
              size="xs"
              onPress={() => {
                setConfirmPaste(false);
                onPasteSettings?.();
              }}
            >
              {t("manualEditor.overwrite")}
            </Button>
            <Button variant="ghost" size="xs" onPress={() => setConfirmPaste(false)}>
              {t("manualEditor.cancel")}
            </Button>
          </span>
        ) : (
          <Button variant="outline" size="xs" onPress={() => setConfirmPaste(true)} className="dark:border-border max-w-56 gap-1.5">
            <ClipboardPaste aria-hidden="true" className="size-3.5" />
            <span className="truncate">
              {t("manualEditor.pasteFrom")} {copiedFrom.label}
            </span>
          </Button>
        )
      ) : null}
      {canDuplicate ? <IconAction label={t("manualEditor.duplicateElement")} icon={<CopyPlus aria-hidden="true" />} onPress={onDuplicate} /> : null}
      {onDelete && !element.isMain && !disabled ? (
        <IconAction label={t("manualEditor.deleteElementButton")} icon={<Trash2 aria-hidden="true" />} onPress={onDelete} isDisabled={busySetting} destructive />
      ) : null}
    </>
  );
  // Only with a real control here. Read-only and file settings keep their collapsible, which says why and has the room.
  const isInline = (name: string) => isInlineSetting(name, onSetSetting != null);
  const inlineSettings = settings.filter((x) => isInline(x.name));
  const collapsibleSettings = settings.filter((x) => !isInline(x.name));
  const settingLabel = (name: string) => {
    const k = SETTING_LABEL_KEYS[name as keyof typeof SETTING_LABEL_KEYS];
    // Unmapped settings fall back to their raw machine name — better than a blank heading.
    return k ? t(k) : name;
  };
  return (
    <div className="flex flex-col gap-3">
      {/* The name is on the element's tab; a second visible heading only took a line (owner 2026-11-02). */}
      <h4 className="sr-only">{label}</h4>
      {actionsSlot ? createPortal(actions, actionsSlot) : <div className="flex flex-wrap items-center justify-end gap-1.5">{actions}</div>}
      {/* A simple setting is a row with its control in plain sight, so changing it takes one click (owner 2026-11-02). */}
      {inlineSettings.length > 0 ? (
        <ul className="m-0 flex list-none flex-col p-0">
          {inlineSettings.map((setting) => (
            <li key={setting.name} className="border-border grid items-center gap-x-3 gap-y-1 border-t py-2 first:border-t-0 first:pt-0 sm:grid-cols-[10rem_minmax(0,1fr)]">
              <span className="text-muted-foreground text-sm">{settingLabel(setting.name)}</span>
              <div className="[&_.mt-2]:mt-0">
                <SettingSection
                  setting={setting}
                  elementId={element.id}
                  disabled={disabled}
                  busy={busySetting === true}
                  onSet={onSetSetting}
                  onPickFile={onPickSettingFile}
                  siblings={siblings}
                  initialStateSensorIds={initialStateSensorIds}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {children}
      {/* What needs room (lists, files, sensor editors) stays collapsible, as in the legacy, one toggle per element. */}
      {collapsibleSettings.length > 0 ? (
        <>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" className="text-muted-foreground h-7 px-2 text-xs" onPress={() => state.setAll(!state.anyOpen)}>
              {t(state.anyOpen ? SECTION_LABELS.collapseAll : SECTION_LABELS.expandAll)}
            </Button>
          </div>
          {/* Two columns where there is room (owner 2026-11-03): one long column left the card's width empty once everything was open. */}
          <ul className={cn("m-0 grid list-none items-start gap-2 p-0", collapsibleSettings.length > 1 && "2xl:grid-cols-2")}>
            {collapsibleSettings.map((setting) => (
              <li key={setting.name}>
                <Disclosure
                  className="border-0"
                  title={
                    <span className="flex items-center gap-2">
                      {settingLabel(setting.name)}
                      {isEditableHere(setting.name, onSetSetting, onPickSettingFile) ? null : (
                        /* Says WHY it cannot be edited yet, rather than looking like a control that does not respond. */
                        <span className="text-muted-foreground border-border rounded-full border px-1.5 text-[0.625rem] font-normal">{t("manualEditor.readOnly")}</span>
                      )}
                    </span>
                  }
                  isExpanded={state.isOpen(setting.name)}
                  onExpandedChange={(open) => state.setOpen(setting.name, open)}
                >
                  <div className="p-3">
                    <SettingSection
                      setting={setting}
                      elementId={element.id}
                      disabled={disabled}
                      busy={busySetting === true}
                      onSet={onSetSetting}
                      onPickFile={onPickSettingFile}
                      siblings={siblings}
                      initialStateSensorIds={initialStateSensorIds}
                    />
                  </div>
                </Disclosure>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <ElementInfoLine element={element} />
    </div>
  );
}

/** A step with no AR element yet — the tab bar is empty, so the body carries the one action that matters. */
export function StepArEmptyBody({ onPickMain, disabled }: { onPickMain?: () => void; disabled: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-2">
      <h4 className="text-muted-foreground m-0 text-sm font-medium">{t("manualEditor.noMainElement")}</h4>
      {onPickMain && !disabled ? (
        <ElementTile element={null} onChange={onPickMain} />
      ) : (
        <ElementTile element={null} />
      )}
    </div>
  );
}

/**
 * One setting, read-only. The legacy renders one container per setting row and dispatches on the NAME
 * (`ARLMS_SettingUIs.js:1859`) — so this is a name switch too, and every element type composes itself from whichever
 * settings it carries. Phase A shows the value; Phase B+ replaces each case with a real control.
 */
function SettingSection({
  setting,
  elementId,
  disabled,
  busy,
  onSet,
  onPickFile,
  siblings,
  initialStateSensorIds,
}: {
  setting: ArElementSetting;
  /** Sensors THIS step is the first to use (ADR-037 D7) — only there is a MultiSwitch's initial state authored. */
  initialStateSensorIds?: ReadonlySet<number>;
  /** The element this setting hangs off — `Path` excludes it from its own endpoint lists. */
  elementId: number;
  /** The STEP is read-only (published gate / no permission). */
  disabled: boolean;
  /** A setting write for this element is in flight — separate from `disabled`, because not every control should
   *  freeze on it: a colour picker that disables itself mid-drag is unusable (diff-panel R1 High #2). */
  busy: boolean;
  onSet?: (name: string, patch: { value?: string | null; fileIds?: number[] }) => void;
  onPickFile?: (name: string, mode: "add" | "replace", dropped?: DroppedFile) => void;
  siblings: ManualArElement[];
}) {
  const { t } = useTranslation();
  const files = setting.files;
  const fields = toFields(setting.json);
  // Which names have a real control yet (ADR-027 Phase B). Everything else stays honestly read-only rather than
  // offering an affordance the server would refuse.
  const editable = onSet != null && EDITABLE_SETTINGS.has(setting.name);
  // Headless: the collapsible that wraps it (ADR-027 §11) carries the title + the read-only pill.
  const switchSetting = SWITCH_SETTINGS[setting.name as keyof typeof SWITCH_SETTINGS] as
    | (typeof SWITCH_SETTINGS)[keyof typeof SWITCH_SETTINGS]
    | undefined;

  return (
    <div className="flex flex-col gap-1">
      {/* One renderer per setting NAME — the legacy's own dispatch (`ARLMS_SettingUIs.js:1859`), which is why an
          element type needs no code of its own: it composes from whichever settings it carries. */}
      {onSet != null && setting.name === "MeshColor" ? <MeshColorControl setting={setting} disabled={disabled} busy={busy} onSet={onSet} /> : null}
      {onSet != null && FILE_SETTINGS.has(setting.name) ? (
        /* No picker ⇒ genuinely read-only here: `disabled` also hides the per-file Remove button, so the
           deferred state is honest rather than an inert control (diff panel). */
        <FileSettingControl setting={setting} disabled={disabled || busy || onPickFile == null} onPickFile={onPickFile} onSet={onSet} />
      ) : null}
      {onSet != null && switchSetting != null ? (
        <SwitchSettingControl setting={setting} disabled={disabled || busy} onSet={onSet} onValue={switchSetting.on} label={t(switchSetting.labelKey)} />
      ) : null}
      {onSet != null && setting.name === "Form" ? <ChoiceSettingControl setting={setting} disabled={disabled || busy} onSet={onSet} options={FORM_OPTIONS} /> : null}
      {onSet != null && setting.name === "AnimationState" ? <AnimationStateControl setting={setting} disabled={disabled || busy} onSet={onSet} /> : null}
      {onSet != null && setting.name === "AreaSize" ? <AreaSizeControl setting={setting} disabled={disabled || busy} onSet={onSet} /> : null}
      {onSet != null && setting.name === "RequiredInteraction" ? <RequiredInteractionControl setting={setting} disabled={disabled || busy} onSet={onSet} /> : null}
      {onSet != null && setting.name === "ManageTraining" ? <ManageTrainingControl setting={setting} disabled={disabled || busy} onSet={onSet} /> : null}
      {onSet != null && setting.name === "Text" ? <TextSettingControl setting={setting} disabled={disabled || busy} onSet={onSet} /> : null}
      {onSet != null && setting.name === "Path" ? <PathControl setting={setting} elementId={elementId} disabled={disabled || busy} onSet={onSet} siblings={siblings} /> : null}
      {/* The sensor kinds (ADR-037). The IDENTITY card renders for every one of them, editable or not — an author
          must be able to see which physical device an element is bound to, which the legacy did not manage for
          `PassivePlateSensorSetting` at all. The body editors follow for the four the legacy has one for. */}
      {SENSOR_SETTINGS.has(setting.name) ? <SensorIdentity setting={setting} /> : null}
      {onSet != null && setting.name === "SwitchSensorSetting" ? <SwitchSensorControl setting={setting} disabled={disabled || busy} onSet={onSet} /> : null}
      {onSet != null && setting.name === "MultiSwitchSensorSetting" ? (
        <MultiSwitchSensorControl setting={setting} disabled={disabled || busy} onSet={onSet} offerInitialState={initialStateSensorIds?.has(setting.sensor?.id ?? -1) ?? false} />
      ) : null}
      {onSet != null && setting.name === "RemoteControlSensorSetting" ? <RemoteControlSensorControl setting={setting} disabled={disabled || busy} onSet={onSet} /> : null}
      {onSet != null && setting.name === "DualSwitchSensorSetting" ? <DualSwitchSensorControl setting={setting} disabled={disabled || busy} onSet={onSet} /> : null}
      {editable ? null : (
        <>
      {files.length > 0 ? (
        <ul className="mt-2 flex list-none flex-col gap-1 p-0">
          {files.map((f) => (
            <li key={f.fileId} className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <Paperclip aria-hidden="true" className="size-3 shrink-0" />
              <span className="truncate">{f.name ?? t("manualEditor.fileN", { id: f.fileId })}</span>
              {f.description != null && f.description !== "" ? <span className="truncate opacity-70">· {f.description}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {/* A parsed object renders as its FIELDS. Still generic — no per-name knowledge — but
          `{"description":"Beantworte…","usesTimer":false,"secondsToFail":0}` as one raw line is barely readable, and
          the parse is already done server-side. Per-name controls replace this in Phase B+. */}
      {fields != null ? (
        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
          {fields.map(([k, v]) => (
            <Fragment key={k}>
              <dt className="text-muted-foreground text-xs">{k}</dt>
              <dd className="m-0 truncate text-xs">{v}</dd>
            </Fragment>
          ))}
        </dl>
      ) : setting.value != null ? (
        /* Legacy setting prose was authored for `innerHTML` and carries raw entities — a real xr-area row reads
           `Alle h&auml;nde`, which React prints literally. `htmlToText` decodes it through an INERT DOMParser (no
           node is ever attached, so nothing loads or executes) and strips tags. Display only: the value on the wire
           is untouched, so nothing here can round-trip a re-encoded string back into the Unity shadow. */
        <p className="text-muted-foreground mt-1 truncate font-mono text-xs">{htmlToText(setting.value)}</p>
      ) : null}
      {fields == null && setting.value == null && files.length === 0 ? <p className="text-muted-foreground/70 mt-1 text-xs italic">Not set</p> : null}
        </>
      )}
    </div>
  );
}

/**
 * `3D Objekt · Farbe` — the owner's mockup, and the legacy's own shape (`CreateMeshColorSettingComponent`): a switch
 * that OVERRIDES the object's colour, and a picker that only exists while the override is on.
 *
 * The legacy encodes "no override" as an EMPTY value rather than a flag, which is why the switch reads
 * `value !== ""` — and why turning it off writes `""` rather than deleting anything.
 */
function MeshColorControl({
  setting,
  disabled,
  busy,
  onSet,
}: {
  setting: ArElementSetting;
  disabled: boolean;
  /** A write for this element is in flight. It gates the SWITCH but deliberately NOT the colour input — see below. */
  busy: boolean;
  onSet: (name: string, patch: { value?: string | null; fileIds?: number[] }) => void;
}) {
  const { t } = useTranslation();
  const on = setting.value != null && setting.value !== "";
  const saved = on ? setting.value! : "#ffffff";
  // Local draft. React maps a colour input's `onChange` to the DOM `input` event, which fires CONTINUOUSLY while the
  // user drags through the picker — wiring that straight to the gate emitted a flushAll + CAS PATCH per intermediate
  // colour, each bumping the manual version (diff-panel R1 High #2). The draft keeps the control responsive; the
  // write happens once, on commit.
  const [colour, setDraft] = useScalarDraft(saved, setting.name);
  const commit = (): void => {
    if (colour !== saved) onSet("MeshColor", { value: colour });
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3">
      <Switch isSelected={on} isDisabled={disabled || busy} onChange={(next) => onSet("MeshColor", { value: next ? colour : "" })} className="text-xs">
        {t("manualEditor.overrideThe3dObjectsColour")}
      </Switch>
      {on ? (
        <span className="flex items-center gap-2">
          {/* No colour PRIMITIVE exists in `components/ui` yet, so this is a deliberate raw `input type=color` — the
              one control the token system cannot express, since its whole job is to render an arbitrary colour. The
              hex is shown beside it as text so the value is readable without a colour-accurate screen. */}
          <input
            type="color"
            aria-label={t("manualEditor.3dObjectColour")}
            value={colour}
            // NOT gated on `busy`: the control disabling itself the moment its own first write is in flight made the
            // picker unusable after one micro-adjustment (diff-panel R1 High #2).
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            className="border-border size-7 cursor-pointer rounded border bg-transparent p-0.5 disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="text-muted-foreground font-mono text-xs">{colour}</span>
        </span>
      ) : null}
    </div>
  );
}

/** Any FILE-valued setting. Reuses the asset library; detach writes an empty list. */
function FileSettingControl({
  setting,
  disabled,
  onPickFile,
  onSet,
}: {
  setting: ArElementSetting;
  disabled: boolean;
  onPickFile?: (name: string, mode: "add" | "replace", dropped?: DroppedFile) => void;
  onSet: (name: string, patch: { value?: string | null; fileIds?: number[] }) => void;
}) {
  const { t } = useTranslation();
  const files = setting.files;
  // A setting is treated as MULTI when its name carries several by design, or when this row simply HAS several — the
  // old control rendered `files[0]` alone, so every other attachment was invisible AND destroyed by the next pick or
  // Remove (the write replaces the whole list). Real corpus: 9 of 12 `TrainerVideos` rows hold 2-3 files, and 11
  // `Image` rows hold 2 (diff-panel R1 High #1a).
  const multi = MULTI_FILE_SETTINGS.has(setting.name) || files.length > 1;
  const { isOver, dropProps } = useSideDrop(
    (drag) => drag.kind === "file" && onPickFile != null && !disabled && fileMatches(drag.name, SETTING_FILE_TYPES[setting.name]),
    (drag) => {
      if (drag.kind === "file") onPickFile?.(setting.name, multi ? "add" : "replace", drag);
    },
    { blockRefused: true },
  );
  return (
    <div {...dropProps} className={cn("mt-2 flex flex-col gap-1.5 rounded-md", isOver && "ring-primary ring-2 ring-offset-2 ring-offset-card")}>
      {files.length === 0 ? <span className="text-muted-foreground/70 text-xs italic">Nothing attached</span> : null}
      {files.map((f) => (
        <div key={f.fileId} className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs">
            <Paperclip aria-hidden="true" className="size-3 shrink-0" />
            <span className="max-w-[14rem] truncate">{f.name ?? t("manualEditor.fileN", { id: f.fileId })}</span>
            {f.description != null && f.description !== "" ? <span className="max-w-[10rem] truncate opacity-70">· {f.description}</span> : null}
          </span>
          {!disabled ? (
            /* Removing ONE file sends the others back, so the wholesale server-side replace keeps them (and their
               captions). The contract has always supported the empty-list detach; there was no control for it. */
            <Button
              variant="ghost"
              size="xs"
              aria-label={t("manualEditor.removeFileid", { fileId: f.name ?? String(f.fileId) })}
              onPress={() => onSet(setting.name, { fileIds: files.filter((x) => x.fileId !== f.fileId).map((x) => x.fileId) })}
              className="text-muted-foreground gap-1.5"
            >
              <X aria-hidden="true" className="size-3" />
              {t("manualEditor.remove")}
            </Button>
          ) : null}
        </div>
      ))}
      {onPickFile && !disabled ? (
        <div>
          <Button variant="outline" size="xs" onPress={() => onPickFile(setting.name, multi ? "add" : "replace")} className="dark:border-border gap-1.5">
            <Boxes aria-hidden="true" className="size-3" />
            {files.length === 0 ? t("manualEditor.choose") : multi ? t("manualEditor.add") : t("manualEditor.replace")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A whole-payload draft for a sensor setting (ADR-037, diff panel High).
 *
 * `useStructuredDraft` compares field by field with `!==`, which is right for its flat primitive payloads but makes
 * an ARRAY field a new reference on every render — the draft would reset during render, which React reports as an
 * infinite loop. Keying on the serialised server object instead gives the same retirement rule (discard the draft on
 * ANY server movement, including a move back to the typed-over values) and works for `targets` / `initalValues`.
 *
 * This is what stops a lost update. A write is enqueued behind `flushAll()`, so the body cache — and therefore
 * `setting.json` — does not move until the round-trip lands, and the disable gate only covers the mutation itself,
 * not the flush wait. Two dropdown picks in that window both built a whole-payload write from the SAME stale read,
 * and the second silently reverted the first. Reproduced in the browser on a real manual: flipping two channels off
 * in quick succession stored `[true, false]`. Every write now merges onto the live draft.
 */
function usePayloadDraft<T extends object>(server: T, key: string): [T, (patch: Partial<T>) => T] {
  // The SERVER fingerprint, distinct from `key` (the draft slot): this one decides RETIREMENT, that one decides
  // where the draft is remembered. Naming both `key` shadowed the parameter and would have stored every payload
  // draft under its own serialized value — a slot that changes the moment the server does, i.e. never restored.
  const seen = JSON.stringify(server);
  const [state, setState] = useScopedDraftState<{ seen: string; patch: Partial<T> }>(key, { seen, patch: {} });
  if (state.seen !== seen) {
    setState({ seen, patch: {} });
    return [server, (patch) => (setState({ seen, patch }), { ...server, ...patch })];
  }
  const value = { ...server, ...state.patch };
  return [
    value,
    (patch) => {
      setState({ seen, patch: { ...state.patch, ...patch } });
      return { ...value, ...patch };
    },
  ];
}

/**
 * The bound device, read-only (ADR-037 D1). The legacy's "Sensor Auswahl" card looks like a picker but is not one:
 * its `sensorType` argument is never used and it renders no list and no button. A sensor is bound when the element
 * is CREATED, in the blueprint picker, and the only way to change it is to delete the element and add it again.
 */
function SensorIdentity({ setting }: { setting: ArElementSetting }) {
  const { t } = useTranslation();
  const sensor = setting.sensor;
  if (sensor == null) {
    return (
      <p className="text-muted-foreground mt-2 max-w-prose text-xs">
        {t("manualEditor.noSensorBound")}
      </p>
    );
  }
  return (
    <dl className="border-border bg-muted/30 mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 rounded-md border p-2">
      <dt className="text-muted-foreground text-[0.625rem]">{t("manualEditor.sensor")}</dt>
      <dd className="m-0 truncate text-xs font-medium">{sensor.displayName ?? t("manualEditor.sensorN", { id: sensor.id })}</dd>
      {sensor.description != null && sensor.description !== "" ? (
        <>
          <dt className="text-muted-foreground text-[0.625rem]">{t("manualEditor.sensorDescription")}</dt>
          <dd className="text-muted-foreground m-0 text-xs">{sensor.description}</dd>
        </>
      ) : null}
    </dl>
  );
}

/** An On/Off dropdown over one boolean — the legacy's `Zielwert` / `Initalwert` control. */
function OnOffSelect({ label, value, disabled, onChange }: { label: string; value: boolean; disabled: boolean; onChange: (next: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <Select className="text-xs" aria-label={label} selectedKey={value ? "on" : "off"} isDisabled={disabled} onSelectionChange={(k) => k != null && onChange(k === "on")}>
      <SelectTrigger />
      <SelectContent>
        <SelectItem id="off">{t("manualEditor.off")}</SelectItem>
        <SelectItem id="on">{t("manualEditor.on")}</SelectItem>
      </SelectContent>
    </Select>
  );
}

/**
 * The two prose fields every sensor body carries. `completedText` gets a REAL control here (ADR-037 D3): in the
 * legacy it had no editor at all and was silently mirrored from a different card's description field, which reached
 * only one of the four kinds and was invisible to the author who triggered it.
 *
 * Each field commits on BLUR, not per keystroke — a keystroke-per-write would be one CAS round-trip each, the same
 * lesson the colour picker taught. The shared draft is what keeps that safe when a sibling field changed first.
 */
function SensorTexts<T extends { setupText: string; completedText: string }>({
  draft,
  server,
  disabled,
  apply,
  onWrite,
}: {
  draft: T;
  server: T;
  disabled: boolean;
  apply: (patch: Partial<T>) => T;
  onWrite: (next: T) => void;
}) {
  const { t } = useTranslation();
  // Controlled by the CARD's draft, not one of its own: a nested draft would be a second source of truth for the
  // same payload, and the two would disagree the moment a dropdown wrote while text was being typed.
  return (
    <div className="flex flex-col gap-2">
      <TextField
        value={draft.setupText}
        isDisabled={disabled}
        onChange={(t) => apply({ setupText: t } as Partial<T>)}
        // Committed on blur, not per keystroke — a keystroke each would be one CAS round-trip each.
        onBlur={() => {
          if (draft.setupText !== server.setupText) onWrite(draft);
        }}
        className="flex max-w-[26rem] flex-col gap-1"
      >
        <Label className="text-muted-foreground text-[0.625rem]">{t("manualEditor.initialState")}</Label>
        <Input className="h-8 text-xs" maxLength={100} />
      </TextField>
      <TextField
        value={draft.completedText}
        isDisabled={disabled}
        onChange={(t) => apply({ completedText: t } as Partial<T>)}
        onBlur={() => {
          if (draft.completedText !== server.completedText) onWrite(draft);
        }}
        className="flex max-w-[26rem] flex-col gap-1"
      >
        {/* In the legacy this field had NO editor at all: it was silently mirrored from a different card's
            description, reaching only one of the four kinds (ADR-037 D3). */}
        <Label className="text-muted-foreground text-[0.625rem]">{t("manualEditor.doneText")}</Label>
        <Input className="h-8 text-xs" maxLength={100} />
      </TextField>
    </div>
  );
}

/** `SwitchSensorSetting` — one channel: its target state, plus the two texts. */
function SwitchSensorControl({ setting, disabled, onSet }: { setting: ArElementSetting; disabled: boolean; onSet: (name: string, patch: { value?: string | null }) => void }) {
  const { t } = useTranslation();
  const server = readSwitch(setting.json);
  const [v, apply] = usePayloadDraft(server, setting.name);
  // Every write merges onto the LIVE draft, never onto the server read — see `usePayloadDraft`.
  const write = (over: Partial<typeof server>) => onSet("SwitchSensorSetting", { value: writeSwitch(apply(over)) });
  return (
    <div className="mt-2 flex flex-col gap-3">
      <div className="flex max-w-[12rem] flex-col gap-1">
        <span className="text-muted-foreground text-[0.625rem]">{t("manualEditor.zielwert")}</span>
        <OnOffSelect label={t("manualEditor.zielwert")} value={v.target} disabled={disabled} onChange={(target) => write({ target })} />
      </div>
      <SensorTexts draft={v} server={server} disabled={disabled} apply={apply} onWrite={(next) => onSet("SwitchSensorSetting", { value: writeSwitch(next) })} />
      <Switch isSelected={v.dontCheckInStepsAfter} isDisabled={disabled} onChange={(next) => write({ dontCheckInStepsAfter: next })} className="text-xs">
        {t("manualEditor.inSpTerenSchrittenNichtMehr")}
      </Switch>
    </div>
  );
}

/** `MultiSwitchSensorSetting` — one row per CHANNEL of the bound device, in the device's own label order. */
function MultiSwitchSensorControl({
  setting,
  disabled,
  onSet,
  offerInitialState,
}: {
  setting: ArElementSetting;
  disabled: boolean;
  onSet: (name: string, patch: { value?: string | null }) => void;
  /** This step is the FIRST to use this device (ADR-037 D7) — only here is the starting state authored. */
  offerInitialState: boolean;
}) {
  const { t } = useTranslation();
  const server = readMulti(setting);
  const [v, apply] = usePayloadDraft(server, setting.name);
  const labels = channelLabels(setting);
  const write = (over: Partial<typeof server>) => onSet("MultiSwitchSensorSetting", { value: writeMulti(apply(over)) });
  const setAt = (key: "targets" | "initalValues", i: number, next: boolean) => {
    // From the DRAFT's array, so flipping a second channel before the first write lands keeps both changes.
    const list = [...v[key]];
    list[i] = next;
    write({ [key]: list });
  };
  if (labels.length === 0) {
    return <p className="text-muted-foreground mt-2 max-w-prose text-xs">{t("manualEditor.noChannelsReported")}</p>;
  }
  return (
    <div className="mt-2 flex flex-col gap-3">
      <ul aria-label={t("manualEditor.kanLe")} className="m-0 flex list-none flex-col gap-2 p-0">
        {labels.map((label, i) => (
          <li key={i} className="flex flex-wrap items-end gap-3">
            <span className="min-w-[8rem] text-xs font-medium">{label ?? t("manualEditor.channelN", { n: i + 1 })}</span>
            <div className="flex w-[10rem] flex-col gap-1">
              <span className="text-muted-foreground text-[0.625rem]">{t("manualEditor.zielwert")}</span>
              <OnOffSelect label={t("manualEditor.targetForChannel", { channel: label ?? t("manualEditor.channelN", { n: i + 1 }) })} value={v.targets[i] === true} disabled={disabled} onChange={(next) => setAt("targets", i, next)} />
            </div>
            {offerInitialState ? (
              <div className="flex w-[10rem] flex-col gap-1">
                <span className="text-muted-foreground text-[0.625rem]">{t("manualEditor.initialValue")}</span>
                <OnOffSelect label={t("manualEditor.initialForChannel", { channel: label ?? t("manualEditor.channelN", { n: i + 1 }) })} value={v.initalValues[i] === true} disabled={disabled} onChange={(next) => setAt("initalValues", i, next)} />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <SensorTexts draft={v} server={server} disabled={disabled} apply={apply} onWrite={(next) => onSet("MultiSwitchSensorSetting", { value: writeMulti(next) })} />
      <Switch isSelected={v.dontCheckInStepsAfter} isDisabled={disabled} onChange={(next) => write({ dontCheckInStepsAfter: next })} className="text-xs">
        {t("manualEditor.inSpTerenSchrittenNichtMehr")}
      </Switch>
    </div>
  );
}

/** `RemoteControlSensorSetting` — the message the device is sent, and how it is triggered. */
function RemoteControlSensorControl({ setting, disabled, onSet }: { setting: ArElementSetting; disabled: boolean; onSet: (name: string, patch: { value?: string | null }) => void }) {
  const { t } = useTranslation();
  const server = readRemote(setting.json);
  const [v, apply] = usePayloadDraft(server, setting.name);
  const write = (over: Partial<typeof server>) => onSet("RemoteControlSensorSetting", { value: writeRemote(apply(over)) });
  return (
    <div className="mt-2 flex flex-col gap-3">
      <Switch isSelected={v.autoPlay} isDisabled={disabled} onChange={(next) => write({ autoPlay: next })} className="text-xs">
        {t("manualEditor.automatischStarten")}
      </Switch>
      <TextField
        value={v.messageToSend}
        isDisabled={disabled}
        onChange={(t) => apply({ messageToSend: t })}
        onBlur={() => {
          if (v.messageToSend !== server.messageToSend) onSet("RemoteControlSensorSetting", { value: writeRemote(v) });
        }}
        className="flex max-w-[20rem] flex-col gap-1"
      >
        {/* The device has to match this verbatim, so the server refuses a value its sanitiser would rewrite rather
            than storing a changed one (ADR-037 D10). */}
        <Label className="text-muted-foreground text-[0.625rem]">{t("manualEditor.messageSent")}</Label>
        <Input className="h-8 font-mono text-xs" maxLength={80} />
      </TextField>
      {/* The legacy always showed this field; it only does anything when the operator has a button to press. */}
      {v.autoPlay ? null : (
        <TextField
          value={v.buttonText}
          isDisabled={disabled}
          onChange={(t) => apply({ buttonText: t })}
          onBlur={() => {
            if (v.buttonText !== server.buttonText) onSet("RemoteControlSensorSetting", { value: writeRemote(v) });
          }}
          className="flex max-w-[20rem] flex-col gap-1"
        >
          <Label className="text-muted-foreground text-[0.625rem]">{t("manualEditor.buttonText")}</Label>
          <Input className="h-8 text-xs" maxLength={80} />
        </TextField>
      )}
      {/* D3 promises these on ALL FOUR kinds. The legacy rendered them for none of them and wrote `completedText`
          only through a hidden mirror, so on this kind the stored values were invisible AND uneditable. */}
      <SensorTexts draft={v} server={server} disabled={disabled} apply={apply} onWrite={(next) => onSet("RemoteControlSensorSetting", { value: writeRemote(next) })} />
    </div>
  );
}

/** `DualSwitchSensorSetting` — the legacy's four fixed combinations over a fixed pair. */
function DualSwitchSensorControl({ setting, disabled, onSet }: { setting: ArElementSetting; disabled: boolean; onSet: (name: string, patch: { value?: string | null }) => void }) {
  const { t } = useTranslation();
  const server = readDual(setting.json);
  const [v, apply] = usePayloadDraft(server, setting.name);
  const write = (over: Partial<typeof server>) => onSet("DualSwitchSensorSetting", { value: writeDual(apply(over)) });
  return (
    <div className="mt-2 flex flex-col gap-3">
      <div className="flex max-w-[18rem] flex-col gap-1">
        <span className="text-muted-foreground text-[0.625rem]">{t("manualEditor.zielwert")}</span>
        <Select
          className="text-xs"
          aria-label={t("manualEditor.zielwert")}
          selectedKey={dualKey(v.targets)}
          isDisabled={disabled}
          onSelectionChange={(k) => {
            const pick = DUAL_CHOICES.find((c) => c.key === String(k));
            if (pick) write({ targets: [...pick.pair] as [boolean, boolean] });
          }}
        >
          <SelectTrigger />
          <SelectContent>
            {DUAL_CHOICES.map((c) => (
              <SelectItem key={c.key} id={c.key}>
                {t(DUAL_LABEL_KEYS[c.key as keyof typeof DUAL_LABEL_KEYS])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <SensorTexts draft={v} server={server} disabled={disabled} apply={apply} onWrite={(next) => onSet("DualSwitchSensorSetting", { value: writeDual(next) })} />
    </div>
  );
}

/** The legacy's four target labels — it hard-coded them in German inside the render; the German here is that
 *  text, and the English is new. Keys, not strings: this map is module-level and cannot call a hook. */
const DUAL_LABEL_KEYS = {
  "off-off": "manualEditor.dual.bothOff",
  "on-off": "manualEditor.dual.firstOnSecondOff",
  "off-on": "manualEditor.dual.firstOffSecondOn",
  "on-on": "manualEditor.dual.bothOn",
} as const;

/** Names that carry SEVERAL files by design — the picker adds to the list instead of replacing it. `TrainerVideos`
 *  labels one video per trainer (9 of 12 live rows hold more than one). */
const MULTI_FILE_SETTINGS = new Set(["TrainerVideos"]);

/** Names that now have a real control. Anything absent keeps its honest `read-only` marker. */
/**
 * Is this setting editable on THIS surface?
 *
 * A file-bearing setting needs a PICKER to be editable at all — without one there is no way to choose a
 * replacement, so its controls must read as read-only rather than offering a Remove button that goes nowhere.
 * The WebGL panel is exactly that case while its picker is deferred (ADR-044 §8 D13); the editor always wires
 * one, so nothing changes there.
 *
 * Found by the diff panel: the panel rendered attached media with a live, destructive-looking Remove button that
 * silently did nothing, because the write guard refused it downstream. A guard that blocks a write is not a
 * substitute for an affordance that never offered it.
 */
function isEditableHere(name: string, onSetSetting: unknown, onPickFile: unknown): boolean {
  if (onSetSetting == null || !EDITABLE_SETTINGS.has(name)) return false;
  return !(FILE_SETTINGS.has(name) && onPickFile == null);
}

const EDITABLE_SETTINGS = new Set([
  "Text",
  "MeshColor",
  "MeshObject",
  "Image",
  "Video",
  "XRCModel",
  "XRCModelAndroid",
  "XRCModelIOS",
  "TrainerVideos",
  "BackgroundPanel",
  "LeftRightDirection",
  "Form",
  "AnimationState",
  "AreaSize",
  "RequiredInteraction",
  "ManageTraining",
  "Path",
  // ADR-037 — the four the legacy has a body editor for. `Cable`/`Motor`/`PassivePlate` deliberately stay out: they
  // get the identity card only, and the server refuses a write to them.
  "SwitchSensorSetting",
  "MultiSwitchSensorSetting",
  "RemoteControlSensorSetting",
  "DualSwitchSensorSetting",
]);

/** Every sensor kind — the identity card renders for all of them, including the three with no body editor. */
const SENSOR_SETTINGS = new Set([
  "SwitchSensorSetting",
  "MultiSwitchSensorSetting",
  "RemoteControlSensorSetting",
  "DualSwitchSensorSetting",
  "CableSensorSetting",
  "MotorSensorSetting",
  "PassivePlateSensorSetting",
]);

/** File-valued names — their identity lives in `files`, so they all share one control. */
/** Settings whose value is an ATTACHMENT, not text. Exported so the WebGL panel can keep exactly these disabled
 *  while its asset picker is deferred (ADR-044 D13) — one list, so the two surfaces cannot disagree about what
 *  counts as a file. */
export const FILE_SETTINGS = new Set(["MeshObject", "Image", "Video", "XRCModel", "XRCModelAndroid", "XRCModelIOS", "TrainerVideos"]);

/** Switch-shaped names. The legacy stores the ON WORD and an EMPTY STRING for off — never a boolean. */
const SWITCH_SETTINGS = {
  BackgroundPanel: { on: "visible", labelKey: "manualEditor.setting.showBackgroundPanel" },
  LeftRightDirection: { on: "left", labelKey: "manualEditor.setting.useLeftDirection" },
} as const satisfies Record<string, { on: string; labelKey: string }>;

/** `ARLMS_SettingUIs.js:3895` — including `SuperslimCube`, which the legacy offers but no live row uses. */
const FORM_OPTIONS = ["Cube", "Sphere", "SlimCube", "SuperslimCube"];


/** A switch whose "off" is an EMPTY value, not `false` — the legacy's own encoding for these names. */
function SwitchSettingControl({
  setting,
  disabled,
  onSet,
  onValue,
  label,
}: {
  setting: ArElementSetting;
  disabled: boolean;
  onSet: (name: string, patch: { value?: string | null }) => void;
  onValue: string;
  label: string;
}) {
  return (
    <div className="mt-2">
      <Switch isSelected={setting.value === onValue} isDisabled={disabled} onChange={(next) => onSet(setting.name, { value: next ? onValue : "" })} className="text-xs">
        {label}
      </Switch>
    </div>
  );
}

/** A fixed vocabulary (`Form`). `Select` is the repo's react-aria primitive — never a native `<select>`. */
function ChoiceSettingControl({
  setting,
  disabled,
  onSet,
  options,
}: {
  setting: ArElementSetting;
  disabled: boolean;
  onSet: (name: string, patch: { value?: string | null }) => void;
  options: string[];
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-2 max-w-[16rem]">
      {/* react-aria's Select cannot DESELECT, so without an explicit entry the control is one-way: once an author
          picks a shape they can never get back to "none", which the server accepts and the legacy offers
          (diff-panel R2 Medium). Same idiom as `Path`'s `Eigene Position`. */}
      <Select
        aria-label={setting.name}
        className="text-xs"
        selectedKey={setting.value == null || setting.value === "" ? UNSET : setting.value}
        isDisabled={disabled}
        onSelectionChange={(k) => k != null && onSet(setting.name, { value: k === UNSET ? "" : String(k) })}
      >
        <SelectTrigger />
        <SelectContent>
          <SelectItem id={UNSET}>{t("manualEditor.noneDash")}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} id={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** `AnimationState` — the choices are the element's OWN `states[]`; only the selection is authored. */
function AnimationStateControl({ setting, disabled, onSet }: { setting: ArElementSetting; disabled: boolean; onSet: (name: string, patch: { value?: string | null }) => void }) {
  const { t } = useTranslation();
  const data = setting.json as { states?: unknown; selectedState?: unknown } | null;
  const states = Array.isArray(data?.states) ? (data.states as unknown[]).filter((x): x is string => typeof x === "string") : [];
  const selected = typeof data?.selectedState === "string" ? data.selectedState : "";
  if (states.length === 0) return <p className="text-muted-foreground/70 mt-1 text-xs italic">{t("manualEditor.noAnimationStates")}</p>;
  return (
    <div className="mt-2 max-w-[16rem]">
      {/* The states list is Unity's, not the author's — re-sent untouched, only the SELECTION changes. */}
      {/* The legacy has this "none" entry too, but COMMENTED OUT (`ARLMS_SettingUIs.js:1905`) while its callback still
          maps it to `""` — so the legacy control is one-way by accident. We keep the entry: the server accepts `""`
          (Unity's "no animation") and a picker you cannot undo is a defect, not parity (diff-panel R2 Medium). */}
      <Select
        aria-label={t("manualEditor.animationState")}
        className="text-xs"
        selectedKey={selected === "" ? UNSET : selected}
        isDisabled={disabled}
        onSelectionChange={(k) =>
          k != null && onSet("AnimationState", { value: JSON.stringify({ states, selectedState: k === UNSET ? "" : String(k) }) })
        }
      >
        <SelectTrigger />
        <SelectContent>
          <SelectItem id={UNSET}>{t("manualEditor.noneDash")}</SelectItem>
          {states.map((sName) => (
            <SelectItem key={sName} id={sName}>
              {sName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** `AreaSize` — three numbers in the geometry convention (`x;y;z`). */
function AreaSizeControl({ setting, disabled, onSet }: { setting: ArElementSetting; disabled: boolean; onSet: (name: string, patch: { value?: string | null }) => void }) {
  const parts = (setting.value ?? "").split(";");
  const at = (i: number): string => (parts[i] ?? "").trim();
  // One draft for the TRIPLE, committed on blur. Per-axis writes each rebuilt the semicolon string from the server's
  // `setting.value`, so editing y and then z sent the old y with the new z (diff-panel R1 High #3) — and every
  // keystroke was its own CAS write.
  const [v, apply] = useStructuredDraft({ x: at(0), y: at(1), z: at(2) }, setting.name);
  const axes = [
    ["x", v.x],
    ["y", v.y],
    ["z", v.z],
  ] as const;
  const commit = (): void => {
    const next = [v.x, v.y, v.z].map((n) => (n.trim() === "" ? "0" : n.trim()));
    if (!next.every((n) => Number.isFinite(Number(n)))) return; // never persist a NaN into the Unity shadow
    const value = next.join(";");
    if (value !== [at(0) || "0", at(1) || "0", at(2) || "0"].join(";")) onSet("AreaSize", { value });
  };
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2">
      {axes.map(([axis, val]) => (
        <TextField key={axis} value={val} isDisabled={disabled} onChange={(t) => apply({ [axis]: t })} onBlur={commit} className="flex w-20 flex-col gap-1">
          <Label className="text-muted-foreground text-[0.625rem]">{axis}</Label>
          <Input inputMode="decimal" className="h-8 text-xs" />
        </TextField>
      ))}
    </div>
  );
}

/** `RequiredInteraction` — the owner's `Verplichtende Checkbox` body: a description, an optional timer, its seconds. */
/**
 * ONE local draft for a STRUCTURED setting — the whole object, not a field at a time (diff-panel R1 High #3).
 *
 * These settings serialise several fields into one JSON value, so every control writes the WHOLE object. Rebuilding
 * that object from the server-rendered props per field loses whatever a sibling field changed but has not yet
 * round-tripped: type a new description, flip `Timed`, and the switch's write carries the OLD description — applied
 * second by the serialised write gate, silently reverting what was just typed.
 *
 * So the draft is the object. `apply` returns the merged result for the caller to write, and a draft key is dropped as
 * soon as the server agrees with it — otherwise our stale local copy would mask a change someone else made.
 */
/**
 * A scalar draft with the same discipline as {@link useStructuredDraft}: it remembers the server value it was typed
 * over and is discarded the moment that value moves. Clearing the draft on blur instead (the first cut) made the
 * control SNAP BACK to the old value for the whole save round-trip — the write is not optimistic, so the server value
 * only changes once the mutation resolves (diff-panel R4 Medium #3).
 */
function useScalarDraft(server: string, key: string): [string, (next: string) => void] {
  // `seen` is the server value this draft was typed over. Any CHANGE of the server value retires the draft — our own
  // write landing, or someone's paste — including a change BACK to the value the draft was typed over. Comparing only
  // `base === server` masked the draft while the server was elsewhere and RESURRECTED it once the server returned to
  // base (A → B → A), and the next blur re-committed the stale B over the pasted A (diff-panel R5 High).
  // Scoped, so an element switch driven by Unity preserves the draft instead of destroying it (ADR-044 §11).
  // With no `DraftScope` above — the main editor — this IS `useState`.
  const [state, setState] = useScopedDraftState<{ seen: string; value: string | null }>(key, { seen: server, value: null });
  if (state.seen !== server) {
    // The documented "adjust state on prop change" pattern — guarded, so it runs once per change.
    setState({ seen: server, value: null });
    return [server, (next) => setState({ seen: server, value: next })];
  }
  return [state.value ?? server, (next) => setState({ seen: server, value: next })];
}

function useStructuredDraft<T extends object>(server: T, key: string): [T, (patch: Partial<T>) => T] {
  // Same retirement rule as `useScalarDraft`: the draft is discarded on ANY change of the server object — including a
  // change back to the values it was typed over (diff-panel R5 High). `seen` is compared field by field.
  const [state, setState] = useScopedDraftState<{ seen: T; patch: Partial<T> }>(key, { seen: server, patch: {} });
  const moved = Object.keys(server).some((k) => state.seen[k as keyof T] !== server[k as keyof T]);
  if (moved) {
    setState({ seen: server, patch: {} });
    return [server, (patch) => (setState({ seen: server, patch }), { ...server, ...patch })];
  }
  const value = { ...server, ...state.patch };
  const apply = (patch: Partial<T>): T => {
    setState({ seen: server, patch: { ...state.patch, ...patch } });
    return { ...value, ...patch };
  };
  return [value, apply];
}

function RequiredInteractionControl({ setting, disabled, onSet }: { setting: ArElementSetting; disabled: boolean; onSet: (name: string, patch: { value?: string | null }) => void }) {
  const { t } = useTranslation();
  const d = (setting.json ?? {}) as { description?: unknown; usesTimer?: unknown; secondsToFail?: unknown };
  const [v, apply] = useStructuredDraft({
    description: typeof d.description === "string" ? d.description : "",
    usesTimer: d.usesTimer === true,
    // The seconds field holds the RAW STRING. Round-tripping it through `Number` on every keystroke made it
    // unclearable (`Number("")` is 0, so it snapped back to "0") and printed `NaN` for a lone "-" (diff-panel R2).
    secondsToFail: String(typeof d.secondsToFail === "number" ? d.secondsToFail : 0),
  }, setting.name);
  const write = (next: { description: string; usesTimer: boolean; secondsToFail: string }): void => {
    const seconds = Number(next.secondsToFail);
    if (!Number.isFinite(seconds) || seconds < 0) return; // never persist NaN into the Unity shadow
    onSet("RequiredInteraction", { value: JSON.stringify({ description: next.description, usesTimer: next.usesTimer, secondsToFail: seconds }) });
  };
  return (
    <div className="mt-2 flex flex-col gap-2">
      <TextField
        value={v.description}
        isDisabled={disabled}
        onChange={(t) => apply({ description: t })}
        // Committed on blur, not per keystroke — every keystroke would be its own CAS write (the lesson from the
        // colour picker, diff-panel R1 High #2). The draft above is what makes that safe across fields.
        onBlur={() => {
          if (v.description !== (typeof d.description === "string" ? d.description : "")) write(v);
        }}
        className="flex max-w-[26rem] flex-col gap-1"
      >
        <Label className="text-muted-foreground text-[0.625rem]">{t("manualEditor.sensorDescription")}</Label>
        <Input className="h-8 text-xs" />
      </TextField>
      <div className="flex flex-wrap items-end gap-3">
        <Switch isSelected={v.usesTimer} isDisabled={disabled} onChange={(next) => write(apply({ usesTimer: next }))} className="text-xs">
          {t("manualEditor.timed")}
        </Switch>
        {v.usesTimer ? (
          <TextField
            value={v.secondsToFail}
            isDisabled={disabled}
            onChange={(t) => apply({ secondsToFail: t })}
            onBlur={() => {
              if (v.secondsToFail !== String(typeof d.secondsToFail === "number" ? d.secondsToFail : 0)) write(v);
            }}
            className="flex w-24 flex-col gap-1"
          >
            <Label className="text-muted-foreground text-[0.625rem]">{t("manualEditor.seconds")}</Label>
            <Input inputMode="numeric" className="h-8 text-xs" />
          </TextField>
        ) : null}
      </div>
    </div>
  );
}

/** `ManageTraining` — `{stepDelay, seperateSections}`. The misspelling is the legacy's and is what Unity reads. */
function ManageTrainingControl({ setting, disabled, onSet }: { setting: ArElementSetting; disabled: boolean; onSet: (name: string, patch: { value?: string | null }) => void }) {
  const { t } = useTranslation();
  const d = (setting.json ?? {}) as { stepDelay?: unknown; seperateSections?: unknown };
  const [v, apply] = useStructuredDraft({
    stepDelay: String(typeof d.stepDelay === "number" ? d.stepDelay : 0), // raw string; parsed only on commit
    seperateSections: d.seperateSections === true,
  }, setting.name);
  const write = (next: { stepDelay: string; seperateSections: boolean }): void => {
    const delay = Number(next.stepDelay);
    if (!Number.isFinite(delay) || delay < 0) return; // never persist NaN into the Unity shadow
    onSet("ManageTraining", { value: JSON.stringify({ stepDelay: delay, seperateSections: next.seperateSections }) });
  };
  return (
    <div className="mt-2 flex flex-wrap items-end gap-3">
      <TextField
        value={v.stepDelay}
        isDisabled={disabled}
        // Draft on change, write on blur — typing "12" used to send a CAS write for "1" and then another for "12",
        // and each rebuilt the object from stale props, dropping whatever the switch beside it had just set.
        onChange={(t) => apply({ stepDelay: t })}
        onBlur={() => {
          if (v.stepDelay !== String(typeof d.stepDelay === "number" ? d.stepDelay : 0)) write(v);
        }}
        className="flex w-28 flex-col gap-1"
      >
        <Label className="text-muted-foreground text-[0.625rem]">{t("manualEditor.stepDelay")}</Label>
        <Input inputMode="numeric" className="h-8 text-xs" />
      </TextField>
      <Switch isSelected={v.seperateSections} isDisabled={disabled} onChange={(next) => write(apply({ seperateSections: next }))} className="text-xs">
        {t("manualEditor.separateSections")}
      </Switch>
    </div>
  );
}

/**
 * `Path` — `{startPointElementId, endPointElementId}`, both referencing elements ON THIS STEP.
 *
 * Same-step in every measured row (662 live: 0 cross-step, 3 dangling), so the pickers offer the step's own elements
 * and nothing else. 566 of those 662 values have UNQUOTED JSON keys, which is why the server parses tolerantly and
 * this control reads `setting.json` rather than the raw string.
 *
 * The candidate lists follow `CreatePathSettingComponent` (`ARLMS_SettingUIs.js:2401`) exactly, and each exclusion is
 * load-bearing:
 *  - **every `Pfad` element is dropped** (`indexesWithoutPath`), not merely this one — a path anchored to another path
 *    has no defined geometry;
 *  - **the two lists exclude each other's current pick**, so start and end can never be the same element;
 *  - **only END offers `-1`** — the legacy's `Eigene Position` ("own position": the arrow free-floats at its own
 *    transform). START has no such entry; it defaults to the step's MAIN element.
 *
 * The legacy REPAIRS a missing/dangling value by writing the default back during render. We derive the same value
 * instead: a render-time write to the Unity shadow would bump the manual version on mere page load and fight the
 * autosave fence. `write()` therefore sends the EFFECTIVE pair, so changing only the End also persists the start the
 * author was shown.
 */
function PathControl({
  setting,
  elementId,
  disabled,
  onSet,
  siblings,
}: {
  setting: ArElementSetting;
  /** The element this `Path` belongs to — excluded from its own endpoint lists. */
  elementId: number;
  disabled: boolean;
  onSet: (name: string, patch: { value?: string | null }) => void;
  siblings: ManualArElement[];
}) {
  const { t } = useTranslation();
  const d = (setting.json ?? {}) as { startPointElementId?: unknown; endPointElementId?: unknown };
  const rawStart = typeof d.startPointElementId === "number" ? d.startPointElementId : -1;
  const rawEnd = typeof d.endPointElementId === "number" ? d.endPointElementId : -1;
  const labels = elementLabels(siblings);
  // Legacy `indexesWithoutPath` + `indexes[i] != objectIndex`: no path element may be an endpoint, this one included.
  const candidates = siblings.filter((e) => e.id !== elementId && !isPathElement(e));
  const mainId = siblings.find((e) => e.isMain)?.id ?? null;
  const known = (id: number): boolean => candidates.some((e) => e.id === id);
  // The legacy's "Start Element does not exist! Fixing Path..." branch, derived rather than written back.
  const start = known(rawStart) ? rawStart : (mainId != null && known(mainId) ? mainId : -1);
  const end = known(rawEnd) ? rawEnd : -1;
  const write = (next: { start?: number; end?: number }): void => {
    const s = next.start ?? start;
    // A start point is always an element (the server refuses `-1` there). With no main element and a dangling stored
    // start there is nothing to derive, so the End picker waits below and this never sends a write the server would
    // 422 (diff-panel R3 High #1).
    if (s === -1) return;
    onSet("Path", { value: JSON.stringify({ startPointElementId: s, endPointElementId: next.end ?? end }) });
  };
  const picker = (label: string, current: number, options: ManualArElement[], ownPosition: boolean, onPick: (id: number) => void, waitsForStart = false) => (
    <div className="flex max-w-[15rem] flex-1 flex-col gap-1">
      <Select
        className="text-xs"
        placeholder="—"
        selectedKey={current === -1 ? (ownPosition ? UNSET : null) : current}
        isDisabled={disabled || (waitsForStart && start === -1)}
        onSelectionChange={(k) => k != null && onPick(k === UNSET ? -1 : Number(k))}
      >
        {/* A VISIBLE `Label` inside `Select`, not a bare `<span>` beside it: react-aria then owns the association and
            the trigger's accessible name composes as `label + current value` ("Hauptelement Start"). A wrapper
            `aria-label` would also reach the trigger, but it names the control only for a screen reader — these two
            pickers need the words "Start"/"End" on screen to be usable at all. */}
        <Label className="text-muted-foreground text-[0.625rem]">{label}</Label>
        <SelectTrigger />
        <SelectContent>
          {ownPosition ? <SelectItem id={UNSET}>{t("manualEditor.ownPosition")}</SelectItem> : null}
          {options.map((e) => (
            <SelectItem key={e.id} id={e.id}>
              {labels.get(e.id) ?? String(e.id)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
  return (
    <div className="mt-2 flex flex-wrap gap-3">
      {picker("Start", start, candidates.filter((e) => e.id !== end), false, (id) => write({ start: id }))}
      {picker("End", end, candidates.filter((e) => e.id !== start), true, (id) => write({ end: id }), true)}
    </div>
  );
}

/** A path element — the legacy matches the blueprint literally named `Pfad` (`ARLMS_SettingUIs.js:2421`); we also
 *  check the element's own name, because a clone inherits the blueprint name and older rows carry it directly. */
function isPathElement(e: ManualArElement): boolean {
  return e.blueprintName === "Pfad" || e.name === "Pfad";
}

/**
 * Which setting names the clipboard can actually carry across — the server's own rules, mirrored so a Paste is only
 * ever offered where it would DO something (diff-panel R4 consensus): `Path` is step-scoped (its endpoints are ids of
 * elements on the source's step) and sensor settings are read-only. Two `Pfad` elements on different steps used to
 * offer a Paste that confirmed, reported success and wrote nothing.
 */
export function isCopyableSetting(name: string): boolean {
  return name !== "Path" && name !== "Goto" && name !== "Clickable" && !name.endsWith("SensorSetting");
}

/** The step's content IS the main element's `Text`/`Image`/`Video` (`assembleBody` derives `step.content` from them),
 *  so the step card already renders these three above. Suppressed on the MAIN tab only — see {@link StepArElementBody}. */
const MAIN_CONTENT_SETTINGS = new Set(["Text", "Image", "Video"]);

/**
 * `Text` on a NON-main element — the body of a `Text` XR element (the main element's Text is the step's Instruction
 * and is suppressed here). The legacy edits it in a plain textarea (`CreateTextSettingComponent`); so do we, committed
 * on blur. The stored value may be a LOCALE MAP (`{"de": …}`): the control edits the German (or first) entry and the
 * server's `mergeLocalizedWrite` folds the plain string back into the map, so no other locale is touched
 * (diff-panel R3 Medium #4 — it was the one body setting with no control at all).
 */
function TextSettingControl({ setting, disabled, onSet }: { setting: ArElementSetting; disabled: boolean; onSet: (name: string, patch: { value?: string | null }) => void }) {
  const { t } = useTranslation();
  const server = localizedText(setting);
  const [value, setValue] = useScalarDraft(server, setting.name);
  return (
    <TextField
      value={value}
      isDisabled={disabled}
      onChange={setValue}
      onBlur={() => {
        // "Text" is the SERVER's setting name, not a label: translating it here would write a German key
        // into the payload and silently drop the edit (diff panel, High).
        if (value !== server) onSet("Text", { value });
      }}
      className="mt-2 flex flex-col gap-1"
    >
      <Label className="text-muted-foreground text-[0.625rem]">{t("manualEditor.text")}</Label>
      <Textarea className="min-h-20 text-xs" />
    </TextField>
  );
}

/** The editable string of a `Text` setting: the `de`/`en`/first entry of a locale map, else the raw value. */
function localizedText(setting: ArElementSetting): string {
  const j = setting.json;
  if (j != null && typeof j === "object" && !Array.isArray(j)) {
    const map = j as Record<string, unknown>;
    const key = "de" in map ? "de" : "en" in map ? "en" : Object.keys(map)[0];
    const v = key != null ? map[key] : undefined;
    if (typeof v === "string") return v;
  }
  return setting.value ?? "";
}

/** The `Path` pickers' "no endpoint" key — `-1` on the wire, but a react-aria key may not be a negative number. */
const UNSET = "__unset__";

/** A parsed setting object → flat `[label, value]` rows. Non-objects (and arrays) fall back to the raw string. */
function toFields(json: unknown): Array<[string, string]> | null {
  if (json == null || typeof json !== "object" || Array.isArray(json)) return null;
  const entries = Object.entries(json as Record<string, unknown>);
  if (entries.length === 0) return null;
  return entries.map(([k, v]) => [k, typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)]);
}

/** Setting-heading KEYS. The German in the catalogue is the legacy's own wording, so an author recognises the
 *  section; the English is new. Keys, not strings — this map is module-level and cannot call a hook. */
const SETTING_LABEL_KEYS = {
  MeshColor: "manualEditor.setting.meshColor",
  MeshObject: "manualEditor.setting.meshObject",
  RequiredInteraction: "manualEditor.setting.requiredInteraction",
  Text: "manualEditor.setting.text",
  Image: "manualEditor.setting.image",
  Video: "manualEditor.setting.video",
  Path: "manualEditor.setting.path",
  AnimationState: "manualEditor.setting.animationState",
  LeftRightDirection: "manualEditor.setting.leftRightDirection",
  AreaSize: "manualEditor.setting.areaSize",
  BackgroundPanel: "manualEditor.setting.backgroundPanel",
  TrainerVideos: "manualEditor.setting.trainerVideos",
  SwitchSensorSetting: "manualEditor.setting.switchSensor",
  MultiSwitchSensorSetting: "manualEditor.setting.multiSwitchSensor",
  RemoteControlSensorSetting: "manualEditor.setting.remoteControlSensor",
  DualSwitchSensorSetting: "manualEditor.setting.dualSwitchSensor",
  CableSensorSetting: "manualEditor.setting.cableSensor",
  MotorSensorSetting: "manualEditor.setting.motorSensor",
  PassivePlateSensorSetting: "manualEditor.setting.passivePlateSensor",
} as const satisfies Record<string, string>;

/** Truncating label with a Tooltip ONLY when it is actually cut off (the repo rule: Tooltip, never native `title`). */
function ElementName({ name, className }: { name: string; className?: string }) {
  const { ref, isTruncated, measure } = useTruncated<HTMLSpanElement>(name);
  const ordinal = /\s\(\d+\)$/.exec(name);
  return (
    <TooltipTrigger delay={0} isDisabled={!isTruncated} excludeFromTabOrder>
      {/* The ordinal is what tells two equal names apart, so only the name in front of it may be cut off. */}
      <span onPointerEnter={measure} className={cn("flex min-w-0", className)}>
        <span ref={ref} className="truncate">
          {ordinal ? name.slice(0, -ordinal[0].length) : name}
        </span>
        {ordinal ? <span className="shrink-0 whitespace-pre">{ordinal[0]}</span> : null}
      </span>
      <Tooltip>{name}</Tooltip>
    </TooltipTrigger>
  );
}

/**
 * Position / Rotation / Size × x·y·z, in the legacy's units (`0m` / `0°` / `1x`).
 *
 * A real `<table>` with real `<th>`s: this is tabular data, and a screen reader reading "Position x 0 metres" only
 * works if the axis and the row are associated as headers. A grid of divs reads as nine loose numbers.
 */
function GeometryTable({ element }: { element: ManualArElement }) {
  const { t } = useTranslation();
  const rows = [
    { label: t("manualEditor.position"), values: element.geometry.position, unit: "m" },
    { label: t("manualEditor.rotation"), values: element.geometry.rotation, unit: "°" },
    { label: t("manualEditor.size"), values: element.geometry.size, unit: "x" },
  ] as const;
  // Information only, so as small as it can be (owner 2026-11-02). Still a table, so each number keeps its axis and its row for a
  // screen reader; the axis letter inside a cell is painted, because the header row already names it.
  const axes = ["x", "y", "z"];
  return (
    <table aria-label={t("manualEditor.placement")} className="block text-xs">
      <thead className="sr-only">
        <tr>
          <td />
          {axes.map((axis) => (
            <th key={axis} scope="col">
              {axis}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="flex flex-wrap gap-x-4 gap-y-0.5">
        {rows.map((row) => (
          <tr key={row.label} className="flex gap-1.5">
            <th scope="row" className="font-medium">
              {row.label}
            </th>
            {row.values.map((v, i) => (
              <td key={`${row.label}-${String(i)}`} className="tabular-nums">
                <span aria-hidden="true" className="text-muted-foreground mr-0.5">
                  {axes[i]}
                </span>
                {fmt(v)}
                <span>{row.unit}</span>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The foot of an element: what its blueprint is for, when that is known, and where it sits. Unity owns the positions. */
function ElementInfoLine({ element }: { element: ManualArElement }) {
  const { descriptionOf } = useElementInfo();
  const description = descriptionOf(element);
  return (
    <div className="border-border text-muted-foreground flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-t pt-2 text-xs">
      {description ? <p className="m-0 line-clamp-2 min-w-0 flex-1 basis-64" title={description}>{description}</p> : null}
      <div className="ml-auto shrink-0 tabular-nums">
        <GeometryTable element={element} />
      </div>
    </div>
  );
}

/** `displayName` when the realm filled one in, else the technical `name` (already German for many takeda rows). */
function elementLabel(element: ManualArElement | null): string {
  if (element == null) return "—";
  const display = element.displayName?.trim();
  return display != null && display !== "" ? display : element.name;
}

/**
 * Per-element labels for ONE step's strip, with an ordinal appended wherever a label repeats.
 *
 * Real data forces this (xr-area step 2697, verified 2026-09-02): two elements, ids 37350 and 37352, share the
 * displayName "Feuerwerk", the technical name `Fireworks`, AND the same parent `Klick-Area`. Nothing intrinsic
 * separates them, so every chip read "Feuerwerk" and both remove buttons announced "Remove Feuerwerk" — a screen-reader
 * user picking between two identical controls, one of which deletes the wrong element. An ordinal is the only
 * disambiguator that always works. Unique labels are left untouched, so takeda's strips are unchanged.
 */
/**
 * Binds ONE selected element to {@link StepArElementBody}'s per-element callbacks from the SCOPED handlers a card
 * receives (step-scoped on `StepCard`, manual-wide on `AllStepsCard`). The two cards used to carry this wiring twice —
 * forty near-identical lines whose only differences are how the scope handlers are addressed and where the duplicate's
 * COPY is found in the returned body. Pure: no hooks, so it is unit-tested directly.
 *
 * - `onPickSettingFile` sends the setting's CURRENT file ids (the picker's add/replace needs them).
 * - `onCopySettings` sends the element's DISPLAY label (`elementLabels` — the "3D Objekt (2)" disambiguation) and only
 *   its copyable settings.
 * - `onDuplicate` resolves the fresh copy as "the one sibling not there before" and selects its tab, as the legacy does
 *   after `DuplicateARElement`; `siblingsAfter` tells it where this scope's siblings live in the returned body.
 */
export function bindElementBody({
  element,
  siblings,
  handlers,
  siblingsAfter,
  onSelect,
}: {
  element: ManualArElement;
  siblings: ManualArElement[];
  handlers: {
    onSetSetting?: (elementId: number, name: string, patch: { value?: string | null; fileIds?: number[] }) => void | Promise<unknown>;
    onPickSettingFile?: (elementId: number, name: string, mode: "add" | "replace", currentFileIds: number[], dropped?: DroppedFile) => void;
    onCopySettings?: (elementId: number, label: string, settingNames: string[]) => void;
    onPasteSettings?: (elementId: number) => void;
    onDuplicate?: (elementId: number) => Promise<ManualBody | void>;
  };
  /** Where THIS scope's siblings sit in a returned body (undefined when the body has no such scope any more). */
  siblingsAfter: (body: ManualBody) => ManualArElement[] | undefined;
  onSelect: (elementId: number) => void;
}): Pick<React.ComponentProps<typeof StepArElementBody>, "onSetSetting" | "onPickSettingFile" | "onCopySettings" | "onPasteSettings" | "onDuplicate"> {
  const { onSetSetting, onPickSettingFile, onCopySettings, onPasteSettings, onDuplicate } = handlers;
  return {
    onSetSetting: onSetSetting ? (name, patch) => void onSetSetting(element.id, name, patch) : undefined,
    onPickSettingFile: onPickSettingFile
      ? (name, mode, dropped) => onPickSettingFile(element.id, name, mode, element.settings.find((x) => x.name === name)?.files.map((f) => f.fileId) ?? [], dropped)
      : undefined,
    onCopySettings: onCopySettings
      ? () => onCopySettings(element.id, elementLabels(siblings).get(element.id) ?? element.name, element.settings.map((x) => x.name).filter(isCopyableSetting))
      : undefined,
    onPasteSettings: onPasteSettings ? () => onPasteSettings(element.id) : undefined,
    onDuplicate: onDuplicate
      ? () => {
          const before = new Set(siblings.map((e) => e.id));
          void onDuplicate(element.id).then((body) => {
            const fresh = body ? siblingsAfter(body)?.find((e) => !before.has(e.id)) : undefined;
            if (fresh) onSelect(fresh.id);
          });
        }
      : undefined,
  };
}

export function elementLabels(elements: ManualArElement[]): Map<number, string> {
  const counts = new Map<string, number>();
  for (const el of elements) {
    const l = elementLabel(el);
    counts.set(l, (counts.get(l) ?? 0) + 1);
  }
  const seen = new Map<string, number>();
  const out = new Map<number, string>();
  for (const el of elements) {
    const l = elementLabel(el);
    if ((counts.get(l) ?? 0) < 2) {
      out.set(el.id, l);
      continue;
    }
    const n = (seen.get(l) ?? 0) + 1;
    seen.set(l, n);
    out.set(el.id, `${l} (${String(n)})`);
  }
  return out;
}

/** Trim the float noise Unity's `"0.0000001;…"` strings carry, without lying about a real offset. */
function fmt(v: number): string {
  if (!Number.isFinite(v)) return "—";
  return String(Math.round(v * 1000) / 1000);
}
